import Price from 'components/price';
import { PriceComposition } from 'components/price-composition';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import orderBy from 'lodash/orderBy';
import { useCartState } from 'modules/cart/state';
import { Row } from 'modules/price-summary/components/row';
import { getCartSubtotal } from 'modules/price-summary/lib/getCartSubtotal';
import { getShippingRowConfig } from 'modules/price-summary/lib/getShippingRowConfig';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface Props {}

export const PriceSummary: FC<Props> = () => {
  const { effectiveCart } = useCartState();
  const { isEc: isExistingCustomer } = useIsExistingCustomer();
  const { formatMessage } = useIntl();

  const cheapestShippingMethod = useMemo(() => {
    const [cheapestShippingMethod] = orderBy(
      effectiveCart?.shippingOptions.methods,
      'price',
      'asc'
    );
    return cheapestShippingMethod;
  }, [effectiveCart]);

  if (!effectiveCart) {
    return null;
  }

  const subtotal = getCartSubtotal(effectiveCart);
  const subtotalAfterDiscounts = effectiveCart.itemsTotal;
  const hasSavings = effectiveCart.itemsTotalReduction > 0;

  const shippingRowConfig = getShippingRowConfig({
    config: {
      cheapestShippingMethod: cheapestShippingMethod,
      isExistingCustomer,
    },
    formatMessage,
  });

  return (
    <div className="mb-4 flex flex-col gap-4">
      <Row
        dataTest={{
          container: 'cart-total',
          price: 'total-price',
          row: 'cart-total-row',
          title: 'tiny-cart-subtotal-title',
        }}
        price={
          <PriceComposition
            className="justify-end gap-3 text-right"
            color="primary"
            currentPrice={subtotalAfterDiscounts}
            priceBefore={hasSavings ? subtotal : undefined}
            cents
            size="lg"
          />
        }
        title={
          <div className="flex flex-col text-lg leading-6 font-black">
            <FormattedMessage
              id={
                hasSavings ? 'cart:tiny-cart:sub-total-with-disscounts' : 'cart:tiny-cart:sub-total'
              }
              values={{
                span: (text) => (
                  <span
                    key="subtotal-after-discounts"
                    className="text-text-light-bg-secondary text-base leading-6 font-normal"
                  >
                    {text}
                  </span>
                ),
              }}
            />
          </div>
        }
      />

      <div data-test="tiny-cart-shipping-section">
        <Row
          dataTest={{
            container: 'price-row-shipping',
            price: 'shipping-price',
            row: 'price-row-shipping-row',
            title: 'formatted-message-shipping',
          }}
          price={
            <span className="text-base leading-6">
              {shippingRowConfig.priceContent ?? <Price cents value={shippingRowConfig.price} />}
            </span>
          }
          title={<span className="text-base leading-6">{shippingRowConfig.title}</span>}
        />
      </div>
    </div>
  );
};
